import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchEntries,
  fetchMyPlayers,
  createEntry,
  updateEntry,
  deleteEntry,
} from './store/timeTrackingSlice';
import ConfirmModal from '../shared/displayComponents/confirmModal/ConfirmModal';
import Loader from '../shared/displayComponents/loader/Loader';

const thStyle = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: 600,
  color: '#475569',
  whiteSpace: 'nowrap',
};

const tdStyle = {
  padding: '10px 12px',
  verticalAlign: 'middle',
  fontSize: '14px',
};

const inputStyle = {
  width: '100%',
  padding: '7px 10px',
  border: '1px solid #cbd5e1',
  borderRadius: '6px',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
};

const btnStyle = {
  padding: '8px 18px',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 600,
};

const today = () => new Date().toISOString().slice(0, 10);

const calcDuration = (start, end) => {
  if (!start || !end) return null;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = eh * 60 + em - (sh * 60 + sm);
  if (mins <= 0) return null;
  return mins;
};

const formatDuration = mins => {
  if (!mins || mins <= 0) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h} godz. ${m} min` : `${m} min`;
};

const calcPay = (mins, rate) => {
  if (!mins || mins <= 0 || !rate) return null;
  return Math.round((mins / 60) * rate * 100) / 100;
};

const currentMonthKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/* Build list of last N months for the filter dropdown */
const buildMonthOptions = (n = 6) => {
  const opts = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const key = `${y}-${m}`;
    const label = new Date(y, d.getMonth(), 1).toLocaleDateString('pl-PL', {
      month: 'long',
      year: 'numeric',
    });
    opts.push({ key, label });
    d.setMonth(d.getMonth() - 1);
  }
  return opts;
};

const EMPTY_FORM_BASE = {
  playerId: '',
  playerName: 'Ogólnie',
  date: today(),
  startTime: '',
  endTime: '',
  notes: '',
};

const TIMER_KEY = 'assistant_active_timer';

const nowTimeStr = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const formatElapsed = (ms) => {
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
};

const AssistantTimeTracking = () => {
  const dispatch = useDispatch();
  const { entries, players, loading, error, defaultRate } = useSelector(
    s => s.timeTracking,
  );

  /* ── Live timer state ── */
  const [timerPlayerId, setTimerPlayerId] = useState('');
  const [activeTimer, setActiveTimer] = useState(() => {
    try { return JSON.parse(localStorage.getItem(TIMER_KEY)) || null; }
    catch { return null; }
  });
  const [elapsed, setElapsed] = useState(0);
  const [timerSubmitting, setTimerSubmitting] = useState(false);
  const intervalRef = useRef(null);

  /* Keep elapsed in sync with activeTimer */
  useEffect(() => {
    if (activeTimer) {
      setElapsed(Date.now() - activeTimer.startedAt);
      intervalRef.current = setInterval(() => {
        setElapsed(Date.now() - activeTimer.startedAt);
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
      setElapsed(0);
    }
    return () => clearInterval(intervalRef.current);
  }, [activeTimer]);

  const startTimer = useCallback(() => {
    const timer = {
      playerId: timerPlayerId || null,
      playerName: timerPlayerId
        ? (() => {
            const p = players.find(pl => String(pl._id) === timerPlayerId);
            return p ? `${p.firstName} ${p.lastName}` : '';
          })()
        : 'Ogólnie',
      date: today(),
      startTime: nowTimeStr(),
      startedAt: Date.now(),
    };
    localStorage.setItem(TIMER_KEY, JSON.stringify(timer));
    setActiveTimer(timer);
  }, [timerPlayerId, players]);

  const stopTimer = useCallback(async () => {
    if (!activeTimer) return;
    const endTime = nowTimeStr();
    localStorage.removeItem(TIMER_KEY);
    setTimerSubmitting(true);
    const result = await dispatch(
      createEntry({
        playerId: activeTimer.playerId,
        playerName: activeTimer.playerName,
        date: activeTimer.date,
        startTime: activeTimer.startTime,
        endTime,
        hourlyRate: defaultRate != null ? Number(defaultRate) : 0,
        notes: '',
      }),
    );
    setTimerSubmitting(false);
    setActiveTimer(null);
    if (!result || !result.success) {
      // store for manual review - will show in form
      setForm(f => ({
        ...f,
        playerId: activeTimer.playerId,
        playerName: activeTimer.playerName,
        date: activeTimer.date,
        startTime: activeTimer.startTime,
        endTime,
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTimer, defaultRate, dispatch]);

  const [form, setForm] = useState({ ...EMPTY_FORM_BASE, hourlyRate: '' });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [sortDir, setSortDir] = useState('desc');
  const [selectedMonth, setSelectedMonth] = useState(currentMonthKey());
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
  });
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, show: false }));

  // Detail modal
  const [detailEntry, setDetailEntry] = useState(null);

  const monthOptions = useMemo(() => buildMonthOptions(12), []);

  useEffect(() => {
    dispatch(fetchMyPlayers());
    dispatch(fetchEntries());
  }, [dispatch]);

  useEffect(() => {
    if (defaultRate != null) {
      setForm(f =>
        f.hourlyRate === '' ? { ...f, hourlyRate: String(defaultRate) } : f,
      );
    }
  }, [defaultRate]);

  const durationMins = useMemo(
    () => calcDuration(form.startTime, form.endTime),
    [form.startTime, form.endTime],
  );
  const previewPay = useMemo(
    () => calcPay(durationMins, Number(form.hourlyRate)),
    [durationMins, form.hourlyRate],
  );

  /* ── Stats for selected month (split by status) ── */
  const monthEntries = useMemo(
    () => entries.filter(e => e.date && e.date.startsWith(selectedMonth)),
    [entries, selectedMonth],
  );

  const approvedMins = useMemo(
    () =>
      monthEntries
        .filter(e => e.status === 'approved')
        .reduce((s, e) => s + (e.durationMinutes || 0), 0),
    [monthEntries],
  );
  const approvedPay = useMemo(
    () =>
      monthEntries
        .filter(e => e.status === 'approved')
        .reduce((s, e) => s + (e.totalPay || 0), 0),
    [monthEntries],
  );
  const pendingMins = useMemo(
    () =>
      monthEntries
        .filter(e => e.status === 'pending')
        .reduce((s, e) => s + (e.durationMinutes || 0), 0),
    [monthEntries],
  );

  /* ── Rejected entries (all time — need attention) ── */
  const rejectedEntries = useMemo(
    () => entries.filter(e => e.status === 'rejected'),
    [entries],
  );

  /* ── Filtered + sorted entries for the table ── */
  const filteredEntries = useMemo(() => {
    let base = entries.filter(e => e.date && e.date.startsWith(selectedMonth));
    if (selectedPlayer) {
      base = base.filter(e => String(e.playerId) === String(selectedPlayer));
    }
    return [...base].sort((a, b) => {
      const cmp = a.date < b.date ? -1 : a.date > b.date ? 1 : 0;
      return sortDir === 'desc' ? -cmp : cmp;
    });
  }, [entries, selectedPlayer, sortDir, selectedMonth]);

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(f => {
      const updated = { ...f, [name]: value };
      if (name === 'playerId') {
        if (value === '') {
          updated.playerName = 'Ogólnie';
        } else {
          const p = players.find(pl => String(pl._id) === value);
          updated.playerName = p ? `${p.firstName} ${p.lastName}` : '';
        }
      }
      return updated;
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setFormError('');
    if (!form.startTime || !form.endTime) {
      setFormError('Podaj godziny.');
      return;
    }
    if (!durationMins || durationMins <= 0) {
      setFormError('Godzina zakończenia musi być późniejsza niż rozpoczęcia.');
      return;
    }
    if (!form.hourlyRate || Number(form.hourlyRate) <= 0) {
      setFormError('Podaj stawkę godzinową.');
      return;
    }

    setSubmitting(true);
    const result = await dispatch(
      createEntry({
        playerId: form.playerId || null,
        playerName: form.playerName,
        date: form.date,
        startTime: form.startTime,
        endTime: form.endTime,
        hourlyRate: Number(form.hourlyRate),
        notes: form.notes,
      }),
    );
    setSubmitting(false);
    if (result && result.success) {
      setForm({
        ...EMPTY_FORM_BASE,
        hourlyRate: defaultRate != null ? String(defaultRate) : '',
      });
    } else {
      setFormError('Nie udało się zapisać wpisu. Spróbuj ponownie.');
    }
  };

  const startEdit = entry => {
    setEditId(entry._id);
    setEditForm({
      date: entry.date,
      startTime: entry.startTime,
      endTime: entry.endTime,
      hourlyRate: entry.hourlyRate,
      notes: entry.notes || '',
    });
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({});
  };

  const saveEdit = async id => {
    const editDur = calcDuration(editForm.startTime, editForm.endTime);
    if (!editDur || editDur <= 0) return;
    await dispatch(
      updateEntry(id, {
        date: editForm.date,
        startTime: editForm.startTime,
        endTime: editForm.endTime,
        hourlyRate: Number(editForm.hourlyRate),
        notes: editForm.notes,
      }),
    );
    setEditId(null);
    setEditForm({});
  };

  const handleDelete = id => {
    setConfirmModal({
      show: true,
      title: 'Usunąć wpis?',
      message: 'Tego działania nie można cofnąć.',
      onConfirm: () => {
        closeConfirm();
        dispatch(deleteEntry(id));
      },
    });
  };

  if (loading && entries.length === 0) {
    return <Loader />;
  }

  if (error) {
    return <div style={{ padding: '32px', color: '#ef4444' }}>{error}</div>;
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1300px' }}>
      {/* Header */}
      <h2
        style={{
          margin: '0 0 16px',
          color: '#1e293b',
          fontSize: '22px',
          fontWeight: 700,
        }}
      >
        Czas pracy
      </h2>

      {/* ── LIVE TIMER ── */}
      <div
        style={{
          background: activeTimer ? 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)' : '#fff',
          border: activeTimer ? 'none' : '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
          <span style={{ fontSize: '18px' }}>⏱</span>
          <span style={{ fontWeight: 700, fontSize: '14px', color: activeTimer ? '#e2e8f0' : '#1e293b' }}>
            Szybki timer
          </span>
        </div>

        {!activeTimer ? (
          <>
            <select
              value={timerPlayerId}
              onChange={e => setTimerPlayerId(e.target.value)}
              style={{
                padding: '7px 10px',
                border: '1px solid #cbd5e1',
                borderRadius: '6px',
                fontSize: '14px',
                minWidth: '180px',
                background: '#fff',
              }}
            >
              <option value="">-- Ogólnie (brak zawodnika) --</option>
              {players.map(p => (
                <option key={p._id} value={p._id}>
                  {p.firstName} {p.lastName}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={startTimer}
              style={{
                padding: '8px 20px',
                background: '#16a34a',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              ▶ Start
            </button>
          </>
        ) : (
          <>
            <div style={{ color: '#94a3b8', fontSize: '14px' }}>
              {activeTimer.playerName} · od {activeTimer.startTime}
            </div>
            <div
              style={{
                fontFamily: 'monospace',
                fontSize: '28px',
                fontWeight: 700,
                color: '#22d3ee',
                letterSpacing: '2px',
                minWidth: '120px',
              }}
            >
              {formatElapsed(elapsed)}
            </div>
            <button
              type="button"
              onClick={stopTimer}
              disabled={timerSubmitting}
              style={{
                padding: '8px 20px',
                background: timerSubmitting ? '#475569' : '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                fontWeight: 700,
                fontSize: '14px',
                cursor: timerSubmitting ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                whiteSpace: 'nowrap',
              }}
            >
              {timerSubmitting ? 'Zapisywanie...' : '⏹ Stop i zapisz'}
            </button>
          </>
        )}
      </div>

      {/* ── WYMAGA UWAGI — Rejected entries ── */}
      {rejectedEntries.length > 0 && (
        <div
          style={{
            background: '#fff7ed',
            border: '1px solid #fdba74',
            borderRadius: '10px',
            padding: '16px 20px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              fontWeight: 700,
              fontSize: '15px',
              color: '#92400e',
              marginBottom: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <span role="img" aria-label="uwaga">⚠️</span>
            <span>Wymaga uwagi — odrzucone wpisy ({rejectedEntries.length})</span>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {rejectedEntries.map(entry => {
              const isEditingHere = editId === entry._id;
              const editDur = isEditingHere
                ? calcDuration(editForm.startTime, editForm.endTime)
                : null;
              const editPay = isEditingHere
                ? calcPay(editDur, Number(editForm.hourlyRate))
                : null;

              return (
                <div
                  key={entry._id}
                  style={{
                    background: '#fff',
                    border: '1px solid #fed7aa',
                    borderRadius: '8px',
                    padding: '14px 16px',
                  }}
                >
                  {!isEditingHere ? (
                    <>
                      {/* Info row */}
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '16px',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontWeight: 600,
                              color: '#1e293b',
                              fontSize: '14px',
                              marginBottom: '4px',
                            }}
                          >
                            {entry.playerName} · {entry.date} · {entry.startTime}–
                            {entry.endTime}
                          </div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>
                            {formatDuration(entry.durationMinutes)} ·{' '}
                            {entry.hourlyRate} PLN/h ·{' '}
                            {entry.totalPay != null
                              ? `${entry.totalPay.toFixed(2)} PLN`
                              : '—'}
                          </div>
                          {entry.notes && (
                            <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '2px' }}>
                              {entry.notes}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => startEdit(entry)}
                          style={{
                            ...btnStyle,
                            background: '#2e496b',
                            color: '#fff',
                            fontSize: '13px',
                            padding: '7px 16px',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <span role="img" aria-label="edytuj">✏️</span> Popraw i prześlij ponownie
                        </button>
                      </div>

                      {/* Admin note */}
                      {entry.adminNote && (
                        <div
                          style={{
                            marginTop: '10px',
                            padding: '8px 12px',
                            background: '#fee2e2',
                            border: '1px solid #fca5a5',
                            borderRadius: '6px',
                            fontSize: '13px',
                            color: '#991b1b',
                          }}
                        >
                          <strong>Uwaga admina:</strong> {entry.adminNote}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Inline edit form inside "Wymaga uwagi" */
                    <div>
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: '14px',
                          color: '#1e293b',
                          marginBottom: '12px',
                        }}
                      >
                        Edytujesz wpis: {entry.playerName}
                        {entry.adminNote && (
                          <span
                            style={{
                              marginLeft: '10px',
                              fontSize: '12px',
                              color: '#991b1b',
                              background: '#fee2e2',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontWeight: 400,
                            }}
                          >
                            Uwaga: {entry.adminNote}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                          gap: '12px',
                          marginBottom: '12px',
                        }}
                      >
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Data</label>
                          <input
                            type="date"
                            value={editForm.date}
                            onChange={e => { const v = e.target.value; setEditForm(f => ({ ...f, date: v })); }}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Od</label>
                          <input
                            type="time"
                            value={editForm.startTime}
                            onChange={e => { const v = e.target.value; setEditForm(f => ({ ...f, startTime: v })); }}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Do</label>
                          <input
                            type="time"
                            value={editForm.endTime}
                            onChange={e => { const v = e.target.value; setEditForm(f => ({ ...f, endTime: v })); }}
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Stawka (PLN/h)</label>
                          <input
                            type="number"
                            value={editForm.hourlyRate}
                            readOnly
                            style={{ ...inputStyle, background: '#f1f5f9', color: '#64748b', cursor: 'default' }}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Czas / Kwota</label>
                          <div style={{ padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#f8fafc', fontSize: '13px', color: '#334155', minHeight: '36px' }}>
                            {editDur && editDur > 0
                              ? `${formatDuration(editDur)} · ${editPay != null ? `${editPay.toFixed(2)} PLN` : '—'}`
                              : <span style={{ color: '#94a3b8' }}>automatycznie</span>}
                          </div>
                        </div>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>Notatki</label>
                        <input
                          type="text"
                          value={editForm.notes}
                          onChange={e => { const v = e.target.value; setEditForm(f => ({ ...f, notes: v })); }}
                          style={inputStyle}
                          placeholder="Opcjonalne notatki..."
                        />
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => saveEdit(entry._id)}
                          style={{ ...btnStyle, background: '#2e496b', color: '#fff', fontSize: '13px' }}
                        >
                          Zapisz i prześlij ponownie
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          style={{ ...btnStyle, background: '#f1f5f9', color: '#475569', fontSize: '13px' }}
                        >
                          Anuluj
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Month stats banner ── */}
      <div
        style={{
          display: 'flex',
          gap: '0',
          background: '#fff',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          marginBottom: '24px',
          overflow: 'hidden',
        }}
      >
        {/* Month selector */}
        <div
          style={{
            padding: '14px 20px',
            borderRight: '1px solid #e2e8f0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
          }}
        >
          <label
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#64748b',
              marginBottom: '4px',
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
            }}
          >
            Miesiąc
          </label>
          <select
            value={selectedMonth}
            onChange={e => setSelectedMonth(e.target.value)}
            style={{
              padding: '5px 8px',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              fontSize: '13px',
              background: '#fff',
              fontWeight: 600,
              color: '#1e293b',
            }}
          >
            {monthOptions.map(opt => (
              <option key={opt.key} value={opt.key}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Approved stats */}
        <div
          style={{
            padding: '14px 24px',
            borderRight: '1px solid #e2e8f0',
            flex: 1,
          }}
        >
          <div
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#166534',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '4px',
            }}
          >
            ✓ Zatwierdzone
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
            {formatDuration(approvedMins)}
          </div>
          <div style={{ fontSize: '13px', color: '#16a34a', fontWeight: 600 }}>
            {approvedPay.toFixed(2)} PLN
          </div>
        </div>

        {/* Pending stats */}
        <div style={{ padding: '14px 24px', flex: 1 }}>
          <div
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#92400e',
              textTransform: 'uppercase',
              letterSpacing: '0.4px',
              marginBottom: '4px',
            }}
          >
            <span role="img" aria-label="oczekuje">⏳</span> Oczekuje zatwierdzenia
          </div>
          <div style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
            {pendingMins > 0 ? formatDuration(pendingMins) : '—'}
          </div>
          <div style={{ fontSize: '13px', color: '#94a3b8' }}>
            {pendingMins > 0
              ? `${monthEntries.filter(e => e.status === 'pending').length} wpisów`
              : 'brak oczekujących'}
          </div>
        </div>
      </div>

      {/* ── Two-column layout ── */}
      <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
        {/* Left: player list */}
        <div
          style={{
            width: '220px',
            flexShrink: 0,
            background: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              padding: '12px 16px',
              fontWeight: 700,
              fontSize: '13px',
              color: '#475569',
              borderBottom: '1px solid #e2e8f0',
            }}
          >
            Moi zawodnicy
          </div>
          <button
            type="button"
            onClick={() => setSelectedPlayer(null)}
            style={{
              display: 'block',
              width: '100%',
              textAlign: 'left',
              padding: '10px 16px',
              border: 'none',
              background: !selectedPlayer ? '#e0f2fe' : 'transparent',
              color: !selectedPlayer ? '#0369a1' : '#1e293b',
              fontWeight: !selectedPlayer ? 700 : 400,
              fontSize: '14px',
              cursor: 'pointer',
              borderBottom: '1px solid #f1f5f9',
            }}
          >
            Wszyscy
          </button>
          {players.map(p => {
            const pid = String(p._id);
            const count = entries.filter(e => String(e.playerId) === pid).length;
            const active = String(selectedPlayer) === pid;
            return (
              <button
                key={pid}
                type="button"
                onClick={() => setSelectedPlayer(active ? null : pid)}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  width: '100%',
                  textAlign: 'left',
                  padding: '10px 16px',
                  border: 'none',
                  background: active ? '#e0f2fe' : 'transparent',
                  color: active ? '#0369a1' : '#1e293b',
                  fontWeight: active ? 700 : 400,
                  fontSize: '14px',
                  cursor: 'pointer',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <span>
                  {p.firstName} {p.lastName}
                </span>
                {count > 0 && (
                  <span
                    style={{
                      background: active ? '#0369a1' : '#cbd5e1',
                      color: active ? '#fff' : '#64748b',
                      borderRadius: '10px',
                      fontSize: '11px',
                      padding: '1px 7px',
                      fontWeight: 600,
                    }}
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
          {players.length === 0 && (
            <div style={{ padding: '16px', color: '#94a3b8', fontSize: '13px' }}>
              Brak przypisanych zawodników.
            </div>
          )}
        </div>

        {/* Right: form + table */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Add entry form */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                fontWeight: 700,
                fontSize: '15px',
                color: '#1e293b',
                marginBottom: '16px',
              }}
            >
              Dodaj wpis czasu
            </div>
            <form onSubmit={handleSubmit}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr',
                  gap: '12px',
                  marginBottom: '12px',
                }}
              >
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                    Zawodnik
                  </label>
                  <select
                    name="playerId"
                    value={form.playerId}
                    onChange={handleFormChange}
                    style={{ ...inputStyle, background: '#fff' }}
                  >
                    <option value="">-- Ogólnie (brak zawodnika) --</option>
                    {players.map(p => (
                      <option key={p._id} value={p._id}>
                        {p.firstName} {p.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                    Data
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleFormChange}
                    style={inputStyle}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                    Stawka (PLN/h)
                  </label>
                  <input
                    type="number"
                    name="hourlyRate"
                    value={form.hourlyRate}
                    readOnly
                    style={{
                      ...inputStyle,
                      background: '#f1f5f9',
                      color: '#64748b',
                      cursor: 'default',
                    }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                    Od
                  </label>
                  <input
                    type="time"
                    name="startTime"
                    value={form.startTime}
                    onChange={handleFormChange}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                    Do
                  </label>
                  <input
                    type="time"
                    name="endTime"
                    value={form.endTime}
                    onChange={handleFormChange}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                    Czas / Wynagrodzenie
                  </label>
                  <div
                    style={{
                      padding: '7px 10px',
                      border: '1px solid #e2e8f0',
                      borderRadius: '6px',
                      background: '#f8fafc',
                      fontSize: '13px',
                      color: '#334155',
                      minHeight: '36px',
                    }}
                  >
                    {durationMins && durationMins > 0 ? (
                      `${formatDuration(durationMins)} · ${
                        previewPay != null ? `${previewPay.toFixed(2)} PLN` : '—'
                      }`
                    ) : (
                      <span style={{ color: '#94a3b8' }}>automatycznie</span>
                    )}
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: '12px' }}>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '4px' }}>
                  Notatki (opcjonalnie)
                </label>
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleFormChange}
                  rows={2}
                  style={{ ...inputStyle, resize: 'vertical' }}
                  placeholder="Opcjonalne notatki..."
                />
              </div>
              {formError && (
                <div style={{ color: '#ef4444', fontSize: '13px', marginBottom: '10px' }}>
                  {formError}
                </div>
              )}
              <button
                type="submit"
                disabled={submitting}
                style={{ ...btnStyle, background: '#2e496b', color: '#fff' }}
              >
                {submitting ? 'Zapisywanie...' : 'Dodaj wpis'}
              </button>
            </form>
          </div>

          {/* Entries table */}
          <div
            style={{
              background: '#fff',
              border: '1px solid #e2e8f0',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 20px',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#1e293b' }}>
                Historia wpisów
                {selectedPlayer &&
                  players.find(p => String(p._id) === selectedPlayer) && (
                    <span
                      style={{
                        marginLeft: '10px',
                        fontSize: '13px',
                        fontWeight: 400,
                        color: '#64748b',
                      }}
                    >
                      —{' '}
                      {(() => {
                        const p = players.find(
                          pl => String(pl._id) === selectedPlayer,
                        );
                        return `${p.firstName} ${p.lastName}`;
                      })()}
                    </span>
                  )}
              </div>
              <button
                type="button"
                onClick={() => setSortDir(d => (d === 'desc' ? 'asc' : 'desc'))}
                style={{
                  ...btnStyle,
                  background: '#f1f5f9',
                  color: '#475569',
                  padding: '5px 12px',
                  fontSize: '12px',
                }}
              >
                Data {sortDir === 'desc' ? '↓' : '↑'}
              </button>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}
              >
                <thead>
                  <tr
                    style={{ borderBottom: '2px solid #e2e8f0', background: '#f8fafc' }}
                  >
                    <th style={thStyle}>Zawodnik</th>
                    <th style={thStyle}>Data</th>
                    <th style={thStyle}>Godziny</th>
                    <th style={thStyle}>Czas</th>
                    <th style={thStyle}>Stawka</th>
                    <th style={thStyle}>Wynagrodzenie</th>
                    <th style={thStyle}>Notatki</th>
                    <th style={thStyle}>Status</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map(entry => (
                    <tr
                      key={entry._id}
                      style={{ borderBottom: '1px solid #f1f5f9', cursor: editId === entry._id ? 'default' : 'pointer' }}
                      onClick={() => { if (editId !== entry._id) setDetailEntry(entry); }}
                    >
                      {editId === entry._id && entry.status !== 'rejected' ? (
                        <>
                          <td style={tdStyle}>{entry.playerName}</td>
                          <td style={tdStyle}>
                            <input
                              type="date"
                              value={editForm.date}
                              onChange={e => {
                                const v = e.target.value;
                                setEditForm(f => ({ ...f, date: v }));
                              }}
                              style={{ ...inputStyle, width: '130px' }}
                            />
                          </td>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                              <input
                                type="time"
                                value={editForm.startTime}
                                onChange={e => {
                                  const v = e.target.value;
                                  setEditForm(f => ({ ...f, startTime: v }));
                                }}
                                style={{ ...inputStyle, width: '90px' }}
                              />
                              <span style={{ color: '#94a3b8' }}>–</span>
                              <input
                                type="time"
                                value={editForm.endTime}
                                onChange={e => {
                                  const v = e.target.value;
                                  setEditForm(f => ({ ...f, endTime: v }));
                                }}
                                style={{ ...inputStyle, width: '90px' }}
                              />
                            </div>
                          </td>
                          <td style={{ ...tdStyle, color: '#64748b' }}>
                            {formatDuration(
                              calcDuration(editForm.startTime, editForm.endTime),
                            )}
                          </td>
                          <td style={tdStyle}>
                            <input
                              type="number"
                              value={editForm.hourlyRate}
                              readOnly
                              style={{
                                ...inputStyle,
                                width: '80px',
                                background: '#f1f5f9',
                                color: '#64748b',
                                cursor: 'default',
                              }}
                            />
                          </td>
                          <td style={{ ...tdStyle, color: '#16a34a', fontWeight: 600 }}>
                            {(() => {
                              const d = calcDuration(
                                editForm.startTime,
                                editForm.endTime,
                              );
                              const p = calcPay(d, Number(editForm.hourlyRate));
                              return p != null ? `${p.toFixed(2)} PLN` : '—';
                            })()}
                          </td>
                          <td style={tdStyle}>
                            <input
                              type="text"
                              value={editForm.notes}
                              onChange={e => {
                                const v = e.target.value;
                                setEditForm(f => ({ ...f, notes: v }));
                              }}
                              style={{ ...inputStyle, width: '140px' }}
                            />
                          </td>
                          <td style={tdStyle} />
                          <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button
                              type="button"
                              onClick={() => saveEdit(entry._id)}
                              style={{
                                ...btnStyle,
                                background: '#16a34a',
                                color: '#fff',
                                padding: '5px 10px',
                                fontSize: '12px',
                                marginRight: '4px',
                              }}
                            >
                              Zapisz
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              style={{
                                ...btnStyle,
                                background: '#f1f5f9',
                                color: '#475569',
                                padding: '5px 10px',
                                fontSize: '12px',
                              }}
                            >
                              Anuluj
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td style={{ ...tdStyle, fontWeight: 500, color: '#1e293b' }}>
                            {entry.playerName}
                          </td>
                          <td style={tdStyle}>{entry.date}</td>
                          <td style={{ ...tdStyle, color: '#64748b' }}>
                            {entry.startTime} – {entry.endTime}
                          </td>
                          <td style={tdStyle}>
                            {formatDuration(entry.durationMinutes)}
                          </td>
                          <td style={tdStyle}>{entry.hourlyRate} PLN/h</td>
                          <td style={{ ...tdStyle, color: '#16a34a', fontWeight: 600 }}>
                            {entry.totalPay != null
                              ? `${entry.totalPay.toFixed(2)} PLN`
                              : '—'}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              maxWidth: '160px',
                            }}
                          >
                            {entry.notes ? (
                              <span
                                style={{
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  background: '#f0f9ff',
                                  border: '1px solid #bae6fd',
                                  borderRadius: '6px',
                                  padding: '2px 8px',
                                  fontSize: '12px',
                                  color: '#0369a1',
                                  fontWeight: 500,
                                  maxWidth: '150px',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  cursor: 'pointer',
                                }}
                                title={entry.notes}
                              >
                                <span style={{ flexShrink: 0 }}>📝</span>
                                {entry.notes}
                              </span>
                            ) : (
                              <span style={{ color: '#cbd5e1' }}>—</span>
                            )}
                          </td>
                          <td style={tdStyle}>
                            {(() => {
                              const statusMap = {
                                approved: {
                                  label: '✓ Zatwierdzone',
                                  bg: '#dcfce7',
                                  color: '#166534',
                                },
                                rejected: {
                                  label: '✗ Odrzucone',
                                  bg: '#fee2e2',
                                  color: '#991b1b',
                                },
                                pending: {
                                  label: '⏳ Oczekuje',
                                  bg: '#fef3c7',
                                  color: '#92400e',
                                },
                              };
                              const s =
                                statusMap[entry.status] || statusMap.pending;
                              return (
                                <div>
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      padding: '2px 8px',
                                      borderRadius: '999px',
                                      fontSize: '11px',
                                      fontWeight: 600,
                                      background: s.bg,
                                      color: s.color,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {s.label}
                                  </span>
                                  {entry.status === 'rejected' && entry.adminNote && (
                                    <div
                                      style={{
                                        fontSize: '11px',
                                        color: '#991b1b',
                                        marginTop: '3px',
                                      }}
                                    >
                                      {entry.adminNote}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td
                            style={{
                              ...tdStyle,
                              textAlign: 'right',
                              whiteSpace: 'nowrap',
                            }}
                            onClick={e => e.stopPropagation()}
                          >
                            {/* Rejected entries are edited from the "Wymaga uwagi" section */}
                            {entry.status !== 'rejected' && (
                              <button
                                type="button"
                                onClick={() => startEdit(entry)}
                                disabled={entry.status === 'approved'}
                                style={{
                                  ...btnStyle,
                                  background: '#e0f2fe',
                                  color: '#0369a1',
                                  padding: '5px 10px',
                                  fontSize: '12px',
                                  marginRight: '4px',
                                  opacity: entry.status === 'approved' ? 0.4 : 1,
                                  cursor:
                                    entry.status === 'approved'
                                      ? 'not-allowed'
                                      : 'pointer',
                                }}
                              >
                                Edytuj
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleDelete(entry._id)}
                              disabled={entry.status === 'approved'}
                              style={{
                                ...btnStyle,
                                background: '#fee2e2',
                                color: '#dc2626',
                                padding: '5px 10px',
                                fontSize: '12px',
                                opacity: entry.status === 'approved' ? 0.4 : 1,
                                cursor:
                                  entry.status === 'approved'
                                    ? 'not-allowed'
                                    : 'pointer',
                              }}
                            >
                              Usuń
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                  {filteredEntries.length === 0 && (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: 'center',
                          padding: '40px',
                          color: '#94a3b8',
                        }}
                      >
                        Brak wpisów za wybrany miesiąc.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Detail modal */}
      {detailEntry && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.45)',
          }}
          onClick={() => setDetailEntry(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
              width: '520px',
              maxWidth: '92vw',
              maxHeight: '85vh',
              overflow: 'auto',
              padding: '28px 32px',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#1e293b' }}>
                Szczegoly wpisu
              </h3>
              <button
                type="button"
                onClick={() => setDetailEntry(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '22px',
                  cursor: 'pointer',
                  color: '#94a3b8',
                  lineHeight: 1,
                  padding: '0 4px',
                }}
              >
                &times;
              </button>
            </div>

            {/* Status badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
              {(() => {
                const statusMap = {
                  approved: { label: 'Zatwierdzone', bg: '#dcfce7', color: '#166534', icon: '✓' },
                  rejected: { label: 'Odrzucone', bg: '#fee2e2', color: '#991b1b', icon: '✗' },
                  pending: { label: 'Oczekuje', bg: '#fef3c7', color: '#92400e', icon: '⏳' },
                };
                const s = statusMap[detailEntry.status] || statusMap.pending;
                return (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      padding: '2px 8px',
                      borderRadius: '999px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: s.bg,
                      color: s.color,
                    }}
                  >
                    {s.icon} {s.label}
                  </span>
                );
              })()}
              {detailEntry.status === 'rejected' && detailEntry.adminNote && (
                <span style={{ fontSize: '13px', color: '#991b1b' }}>
                  {detailEntry.adminNote}
                </span>
              )}
            </div>

            {[
              ['Zawodnik', detailEntry.playerName],
              ['Data', detailEntry.date],
              ['Godziny', `${detailEntry.startTime} – ${detailEntry.endTime}`],
              ['Czas', formatDuration(detailEntry.durationMinutes)],
              ['Stawka', detailEntry.hourlyRate != null ? `${detailEntry.hourlyRate} PLN/h` : '—'],
              ['Wynagrodzenie', detailEntry.totalPay != null ? `${detailEntry.totalPay.toFixed(2)} PLN` : '—'],
            ].map(([label, value]) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  padding: '8px 0',
                  borderBottom: '1px solid #f1f5f9',
                }}
              >
                <span style={{ width: '130px', flexShrink: 0, fontWeight: 600, color: '#475569', fontSize: '13px' }}>
                  {label}
                </span>
                <span style={{ color: '#1e293b', fontSize: '14px' }}>{value}</span>
              </div>
            ))}

            {/* Notes — full display */}
            <div style={{ marginTop: '16px' }}>
              <div style={{ fontWeight: 600, color: '#475569', fontSize: '13px', marginBottom: '6px' }}>
                Notatki
              </div>
              <div
                style={{
                  background: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  fontSize: '14px',
                  color: '#1e293b',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  lineHeight: 1.6,
                  minHeight: '48px',
                }}
              >
                {detailEntry.notes || <span style={{ color: '#cbd5e1' }}>Brak notatek</span>}
              </div>
            </div>

            {/* Admin note if rejected */}
            {detailEntry.status === 'rejected' && detailEntry.adminNote && (
              <div style={{ marginTop: '16px' }}>
                <div style={{ fontWeight: 600, color: '#991b1b', fontSize: '13px', marginBottom: '6px' }}>
                  Uwaga admina
                </div>
                <div
                  style={{
                    background: '#fee2e2',
                    border: '1px solid #fca5a5',
                    borderRadius: '8px',
                    padding: '12px 16px',
                    fontSize: '14px',
                    color: '#991b1b',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                  }}
                >
                  {detailEntry.adminNote}
                </div>
              </div>
            )}

            <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setDetailEntry(null)}
                style={{ ...btnStyle, background: '#f1f5f9', color: '#475569' }}
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm || (() => {})}
        onCancel={closeConfirm}
        confirmLabel="Usuń"
        icon="fas fa-trash-alt"
        danger
      />
    </div>
  );
};

export default AssistantTimeTracking;
